import React, { useState, useEffect } from 'react';
import { Spinner, Table, Modal, Icon } from '../../../../core/ui';
import axios from '../../../../core/api/client';
import { createShareLink, revokeShareLink, getShareStatus } from '../../api/shared';
import { useTranslation } from '../../../../core/context/TranslationContext';
import CardChargeNote from './CardChargeNote';
import styles from './ReceiptDetailPanel.module.css';

const ReceiptDetailPanel = ({ receipt, cardLink, onEdit, onDelete, onClose, readOnly = false }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (receipt) {
      const fetchItems = async () => {
        setLoading(true);
        try {
          const response = await axios.get(`/api/receipts/${receipt.id}/items`);
          setItems(response.data);
          setError(null);
        } catch (err) {
          setError(t('errorFetchingItems'));
        } finally {
          setLoading(false);
        }
      };
      fetchItems();

      if (!readOnly) {
        getShareStatus(receipt.id).then(res => {
          setShareToken(res.data.shared ? res.data.shareToken : null);
        }).catch(() => setShareToken(null));
      }
      setShareCopied(false);
    }
  }, [receipt, t, readOnly]);

  const formatCurrency = (amount) => {
    if (isNaN(amount)) return '-';
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR');
    } catch {
      return dateStr;
    }
  };

  const handleViewImage = async () => {
    setImageLoading(true);
    setShowImageModal(true);
    try {
      const response = await axios.get(`/api/receipts/${receipt.id}/image`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      setImageUrl(url);
    } catch (err) {
      setImageUrl(null);
    } finally {
      setImageLoading(false);
    }
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
      setImageUrl(null);
    }
  };

  const handleShare = async () => {
    try {
      const res = await createShareLink(receipt.id);
      const token = res.data.shareToken;
      setShareToken(token);
      setShowShareModal(true);
    } catch (err) {
      // ignore
    }
  };

  const handleUnshare = async () => {
    try {
      await revokeShareLink(receipt.id);
      setShareToken(null);
      setShowShareModal(false);
    } catch (err) {
      // ignore
    }
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/shared/${shareToken}`;
    navigator.clipboard.writeText(url);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  return (
    <>
      <div className={`${styles.sidePanel} ${receipt ? styles.open : ''}`}>
        <div className={styles.panelHeader}>
          <div>
            <h5>
              <Icon name="fa-store" className={styles.iconMeta} />
              {receipt?.storeName}
            </h5>
            <div className={styles.headerMeta}>
              <span>
                <Icon name="fa-calendar-alt" />
                {formatDate(receipt?.purchaseDateTime)}
              </span>
              <span>
                <Icon name="fa-coins" />
                {formatCurrency(receipt?.totalAmount)}
              </span>
            </div>
            <div className={styles.actions}>
              <button className={styles.actionBtn} onClick={handleViewImage} title={t('viewReceipt')}>
                <Icon name="fa-eye" size={15} />
                <span>{t('viewReceipt')}</span>
              </button>
              {!readOnly && (
                <>
                  <button className={styles.actionBtn} onClick={() => onEdit(receipt)} title={t('edit')}>
                    <Icon name="fa-pencil" size={15} />
                    <span>{t('edit')}</span>
                  </button>
                  <button className={`${styles.actionBtn} ${styles.actionBtnDanger}`} onClick={() => onDelete(receipt.id)} title={t('delete')}>
                    <Icon name="fa-trash" size={15} />
                    <span>{t('delete')}</span>
                  </button>
                  <button className={styles.actionBtn} onClick={shareToken ? () => setShowShareModal(true) : handleShare} title={shareToken ? t('shareLink') : t('share')}>
                    <Icon name="fa-share-alt" size={15} />
                    <span>{shareToken ? t('shareLink') : t('share')}</span>
                  </button>
                </>
              )}
            </div>
            <CardChargeNote link={cardLink} />
          </div>
          <button onClick={onClose} className={styles.closeButton}>
            <Icon name="fa-times" size={24} />
          </button>
        </div>
        <div className={styles.panelBody}>
          {loading ? (
            <div className={styles.loadingWrap}>
              <Spinner label={t('loading')} />
            </div>
          ) : error ? (
            <div className={styles.error}>{error}</div>
          ) : (
            <Table hover>
              <thead>
                <tr>
                  <th>{t('product')}</th>
                  <th>{t('quantity')}</th>
                  <th>{t('totalPrice')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id}>
                    <td>{item.productName}</td>
                    <td>{item.quantity}</td>
                    <td>{formatCurrency(item.totalPrice)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      <Modal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        title={t('shareReceipt')}
      >
        {shareToken && (
          <div>
            <div className={styles.inputGroup}>
              <input
                type="text"
                className={styles.shareInput}
                readOnly
                value={`${window.location.origin}/shared/${shareToken}`}
              />
              <button className={`${styles.modalBtn} ${shareCopied ? styles.modalBtnSuccess : ''}`} onClick={handleCopyLink}>
                {shareCopied ? <><Icon name="fa-check" />{t('linkCopied')}</> : t('copyLink')}
              </button>
            </div>
            <button className={`${styles.modalBtn} ${styles.modalBtnDanger}`} onClick={handleUnshare}>
              {t('unshare')}
            </button>
          </div>
        )}
      </Modal>

      <Modal
        open={showImageModal}
        onClose={handleCloseImageModal}
        title={receipt?.storeName}
        size="lg"
      >
        <div className={styles.imageContainer}>
          {imageLoading ? (
            <Spinner label={t('loading')} />
          ) : imageUrl ? (
            <img src={imageUrl} alt="receipt" className={styles.receiptImage} />
          ) : (
            <p className={styles.errorText}>{t('errorFetchingItems')}</p>
          )}
        </div>
      </Modal>
    </>
  );
};

export default ReceiptDetailPanel;
